import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  applyThemeTokens,
  buildTheme,
  getStoredMode,
  setCurrentMode,
  THEME_STORAGE_KEY,
  ThemeMode,
} from "./theme";

// "system" means no explicit choice is stored, so the OS preference wins.
export type ThemePreference = ThemeMode | "system";

type ThemeModeContextValue = {
  mode: ThemeMode;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "light",
  preference: "system",
  setPreference: () => {},
  toggleMode: () => {},
});

const systemMode = (): ThemeMode =>
  window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";

export const useThemeMode = () => useContext(ThemeModeContext);

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const initial = getStoredMode();
    // Keep the module mirror and CSS vars correct on the very first render,
    // before the effect below runs.
    setCurrentMode(initial);
    applyThemeTokens(initial);
    return initial;
  });

  useEffect(() => {
    setCurrentMode(mode);
    applyThemeTokens(mode);
  }, [mode]);

  const [preference, setPreferenceState] = useState<ThemePreference>(() => {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  });

  const setPreference = useCallback((next: ThemePreference) => {
    if (next === "system") {
      window.localStorage.removeItem(THEME_STORAGE_KEY);
      setPreferenceState("system");
      setModeState(systemMode());
      return;
    }
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
    setPreferenceState(next);
    setModeState(next);
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next: ThemeMode = prev === "light" ? "dark" : "light";
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
      setPreferenceState(next);
      return next;
    });
  }, []);

  // Follow the OS preference until the user makes an explicit choice.
  useEffect(() => {
    const query = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!query) return;
    const onChange = (e: MediaQueryListEvent) => {
      if (window.localStorage.getItem(THEME_STORAGE_KEY)) return;
      setModeState(e.matches ? "dark" : "light");
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const theme = useMemo(() => buildTheme(mode), [mode]);
  const value = useMemo(
    () => ({ mode, preference, setPreference, toggleMode }),
    [mode, preference, setPreference, toggleMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
